<template>
  <v-row no-gutters :style="'justify-content:' + colsAlign">
    <v-col :cols="cols" :style="'height:' + height">
      <v-skeleton-loader
        v-if="!frontReady"
        type="image"
        :style="'height:' + height + '; borderRadius:' + borderRadius"
        style="width: 100%"></v-skeleton-loader>
      <object
        class="interactive-human-body__svg"
        :class="margin"
        :style="{ position: backReady ? 'relative' : 'absolute', background: background, borderRadius: borderRadius, opacity: frontReady ? 1 : 0 }"
        @load="frontInit"
        :id="'front' + split"
        type="image/svg+xml"
        data="/img/einmalig/humanSVGFrontPlanRun.svg"></object>
    </v-col>

    <v-col :cols="cols" :style="'height:' + height">
      <v-skeleton-loader
        v-if="!backReady"
        type="image"
        :style="'height:' + height + '; borderRadius:' + borderRadius"
        style="width: 100%"></v-skeleton-loader>
      <object
        :id="'back' + split"
        :class="margin"
        :style="{ position: backReady ? 'relative' : 'absolute', background: background, borderRadius: borderRadius, opacity: backReady ? 1 : 0 }"
        class="interactive-human-body__svg"
        type="image/svg+xml"
        @load="backInit"
        data="/img/einmalig/humanSVGBackPlanRun.svg"></object>
    </v-col>

    <v-col cols="12" v-if="!hideTagWindows">
      <v-window v-model="tagWindows">
        <v-window-item v-for="activeClass of activeClasses" :value="activeClass" :key="`card-${activeClass}`">
          <v-card color="#f5f5f5" class="rounded-xl ma-5">
            <v-card-title v-if="tags.find((filter) => filter.class === activeClass)">
              {{ tags.find((filter) => filter.class === activeClass).name }}
              <v-chip class="ml-2">
                {{ getExercisesByTag(activeClass).length + (getExercisesByTag(activeClass).length > 1 ? " Übungen" : " Übung") }}
              </v-chip>
            </v-card-title>
            <v-row no-gutters style="max-height: 200px; overflow: auto">
              <v-col class="pa-3" cols="12" v-for="(exercise, index) of getExercisesByTag(activeClass)" :key="'workoutEx' + index">
                <div style="display: flex">
                  <v-col cols="3">
                    <v-img class="rounded" :src="exercise.img"></v-img>
                  </v-col>
                  <v-col cols="9">
                    <v-card-title class="h6 py-1">{{ exercise.title }}</v-card-title>
                    <v-card-subtitle class="py-1">{{ exercise.device }}</v-card-subtitle>
                  </v-col>
                </div>
                <v-divider class="my-0"></v-divider>
              </v-col>
            </v-row>
          </v-card>
        </v-window-item>
      </v-window>
    </v-col>
  </v-row>
</template>
<script>
import VueFlip from "vue-flip"
import api from "@/api"
import { mapGetters, mapActions } from "vuex"

export default {
  name: "interactiveHumanBody",
  components: {
    VueFlip,
  },
  props: {
    noTouch: { type: Boolean, default: false },
    height: { type: String, default: "230px" },
    updateInComponent: { type: Boolean, default: false },
    cols: { type: [Number, String], default: 6 },
    background: { type: String, default: "white" },
    borderRadius: { type: String, default: "0px" },
    margin: { type: String, default: "mx-0" },
    colsAlign: { default: "space-around" },
    exercises: {},
    split: { type: Number, default: 0 },
    activeClasses: { type: Array, default: () => [] },
    hideTagWindows: { type: Boolean, default: false },
  },
  data() {
    return {
      tagWindows: null,
      tags: [],
      filters: [],
      initDone: false,

      frontReady: false,
      backReady: false,
      // Hinzugefügt: Referenzen auf die SVG-Dokumente
      frontSvgDoc: null,
      backSvgDoc: null,
    }
  },
  async created() {
    this.tags = await api.fetchExerciseTags()
    this.processFilters()
    // attemptInitialization wird nun von frontInit/backInit zuverlässiger aufgerufen
  },
  methods: {
    processFilters() {
      if (!this.tags.length || !this.activeClasses?.length) {
        this.filters = JSON.parse(JSON.stringify(this.activeClasses || []))
        return
      }

      this.filters = this.activeClasses
        .map((filterClass) => {
          const tag = this.tags.find((t) => t.class === filterClass || t.name === filterClass)
          return tag ? tag.class : null
        })
        .filter(Boolean)

      if (this.filters.length) {
        this.tagWindows = this.filters[0]
      }
    },

    // Überarbeitete Initialisierungslogik
    attemptInitialization() {
      // Guard: Nur ausführen, wenn alles bereit ist und noch nicht initialisiert wurde
      if (this.frontReady && this.backReady && this.filters.length > 0 && !this.initDone) {
        // Caching der SVG-Dokument-Referenzen
        this.frontSvgDoc = document.getElementById("front" + this.split)?.getSVGDocument()
        this.backSvgDoc = document.getElementById("back" + this.split)?.getSVGDocument()

        // Zusätzliche Prüfung, ob die Dokumente wirklich verfügbar sind
        if (!this.frontSvgDoc || !this.backSvgDoc) {
          console.error("SVG documents not ready for scripting yet.")
          // Optional: Hier könnte man einen erneuten Versuch nach kurzer Zeit starten
          return
        }

        this.initDone = true
        this.initializeSVGColors()
        this.addClickListeners()
      }
    },

    initializeSVGColors() {
      // Verwende die gecachten Referenzen
      if (!this.frontSvgDoc || !this.backSvgDoc) return

      this.filters.forEach((filter) => {
        const allElements = [...this.frontSvgDoc.getElementsByClassName(filter), ...this.backSvgDoc.getElementsByClassName(filter)]
        for (let element of allElements) {
          element.setAttribute("fill", "red")
        }
      })
    },

    addClickListeners() {
      if (this.noTouch || !this.frontSvgDoc || !this.backSvgDoc) return

      // Füge Listener für Klicks und Touches hinzu
      const handleEvent = (e) => this.handleSvgClick(e)

      this.frontSvgDoc.addEventListener("click", handleEvent)
      this.frontSvgDoc.addEventListener("touchend", handleEvent) // Wichtig für iOS

      this.backSvgDoc.addEventListener("click", handleEvent)
      this.backSvgDoc.addEventListener("touchend", handleEvent) // Wichtig für iOS
    },

    handleSvgClick(event) {
      // Verhindere, dass sowohl touchend als auch click kurz nacheinander ausgelöst werden
      event.preventDefault()
      event.stopPropagation()

      // Verwende die gecachten Referenzen
      if (!this.frontSvgDoc || !this.backSvgDoc) return

      // event.target könnte ein Kind-Element sein, wir suchen den nächsten Elter mit einer Klasse
      const targetElement = event.target.closest("[class]")
      if (!targetElement) return

      targetElement.classList.forEach((klasse) => {
        if (klasse === "unselectable" || klasse === "seitliche-schulter") return

        const allElements = [...this.frontSvgDoc.getElementsByClassName(klasse), ...this.backSvgDoc.getElementsByClassName(klasse)]
        if (allElements.length === 0) return

        if (this.updateInComponent) {
          const tag = this.tags.find((t) => t.class === klasse)
          if (this.filters.includes(klasse)) {
            this.filters = this.filters.filter((f) => f !== klasse)
            this.$emit("removeElement", tag.name)
            allElements.forEach((el) => el.setAttribute("fill", "white")) // Oder zurück zur Originalfarbe
          } else {
            this.filters.push(klasse)
            this.$emit("addElement", tag.name)
            allElements.forEach((el) => el.setAttribute("fill", "red"))
          }
        }

        this.highlightSelection(klasse)
      })
    },

    highlightSelection(activeClass) {
      if (!this.frontSvgDoc || !this.backSvgDoc) return

      // Alle markierten Elemente zurücksetzen (ausser die neue Auswahl)
      this.filters.forEach((filter) => {
        if (filter !== activeClass) {
          const elements = [...this.frontSvgDoc.getElementsByClassName(filter), ...this.backSvgDoc.getElementsByClassName(filter)]
          elements.forEach((el) => el.setAttribute("fill", "red"))
        }
      })

      // Das aktuell geklickte/aktive Element hervorheben
      if (this.filters.includes(activeClass)) {
        const activeElements = [...this.frontSvgDoc.getElementsByClassName(activeClass), ...this.backSvgDoc.getElementsByClassName(activeClass)]
        activeElements.forEach((el) => el.setAttribute("fill", "gold"))
      }

      this.tagWindows = activeClass
    },

    // Robuste Init-Methoden, die auf die Verfügbarkeit des SVG-DOMs warten
    initSvg(side) {
      let attempts = 0
      const maxAttempts = 50 // Versuche es für 5 Sekunden (50 * 100ms)

      const interval = setInterval(() => {
        const svgDoc = document.getElementById(side + this.split)?.getSVGDocument()

        if (svgDoc) {
          clearInterval(interval)
          if (side === "front") {
            this.frontReady = true
          } else {
            this.backReady = true
          }
          this.attemptInitialization()
        } else if (++attempts > maxAttempts) {
          clearInterval(interval)
          console.error(`Could not initialize ${side} SVG after ${maxAttempts} attempts.`)
        }
      }, 100)
    },

    frontInit() {
      this.initSvg("front")
    },

    backInit() {
      this.initSvg("back")
    },

    getExercisesByTag(tag) {
      if (!this.exercises) return []
      return this.exercises.filter((exercise) => {
        return exercise.tags.find((exTag) => exTag.class === tag)
      })
    },
  },

  watch: {
    filters(newValue) {
      this.$emit("filters", newValue)
    },
    activeClasses: {
      handler() {
        this.processFilters()
        // Wenn die SVGs bereits geladen sind, versuche sofort zu initialisieren
        if (this.frontReady && this.backReady) {
          this.attemptInitialization()
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-dialog {
  background: white;
}
.unselectable {
  fill: white;
}

.interactive-human-body {
  &__flip {
    position: relative;
  }
  &__svg {
    width: 100%;
    height: 100%;
  }
}
</style>
